import type { TreeData } from '@revelio/ui';

import type { AllSelectionListsWithSkillTaxonomyKey } from '../types/filters';

type NestedTaxonomyTreeNode = TreeData<{
  levelId: AllSelectionListsWithSkillTaxonomyKey;
}>;

const isValidTaxonomyNodeName = (name: string) => {
  const normalizedName = name.trim().toLowerCase();
  return normalizedName !== '' && normalizedName !== 'empty';
};

export const toNestedTaxonomyTree = ({
  nodes,
  selectionListIds,
  parentId,
  includeNodeMetadata = true,
  filterInvalidNodeNames = false,
  visibleDepth = 0,
}: {
  nodes: TreeData[];
  selectionListIds: readonly AllSelectionListsWithSkillTaxonomyKey[];
  parentId?: string;
  includeNodeMetadata?: boolean;
  filterInvalidNodeNames?: boolean;
  visibleDepth?: number;
}): NestedTaxonomyTreeNode[] => {
  const result: NestedTaxonomyTreeNode[] = [];

  for (const node of nodes) {
    if (filterInvalidNodeNames && !isValidTaxonomyNodeName(node.name)) {
      continue;
    }

    const id = parentId ? `${parentId}_${node.id}` : node.id;
    const childParentId = id;

    let children: NestedTaxonomyTreeNode[] | undefined;
    if (node.children && node.children.length > 0) {
      children = toNestedTaxonomyTree({
        nodes: node.children,
        selectionListIds,
        parentId: childParentId,
        includeNodeMetadata,
        filterInvalidNodeNames,
        visibleDepth: visibleDepth + 1,
      });
      if (children.length === 0) {
        children = undefined;
      }
    }

    const levelId =
      selectionListIds[visibleDepth] ??
      selectionListIds[selectionListIds.length - 1];
    if (levelId === undefined) {
      continue;
    }

    if (includeNodeMetadata) {
      const { children: _ignoredChildren, ...nodeWithoutChildren } = node;
      result.push({
        ...nodeWithoutChildren,
        id,
        levelId,
        ...(children ? { children } : {}),
      });
    } else {
      result.push({
        id,
        name: node.name,
        levelId,
        ...(node.depth !== undefined ? { depth: node.depth } : {}),
        ...(children ? { children } : {}),
      });
    }
  }

  return result;
};
